import { useEffect, useMemo, useState } from 'react'
import { gql, useQuery } from '@apollo/client'
import { CaptionMD, TitleSM } from '@/atoms/Text'
import { paths } from '@/constants/paths'
import { AngelDialog } from '@/molecules/AngelDialog'
import { LargeLandscapeGrid } from '@/page/CollectionDisplay/LargeLandscapeGrid'
import { CONTENT_AVAIL_FRAG } from '@/services/ContentAvailabilityService'
import { GetBrandShareDataQuery, GetBrandShareDataQueryVariables } from '@/types/codegen-federation'
import { Translate, useTranslate } from '@/utils/translate/translate-client'

interface WatchProjectShareSeriesModalProps {
  slug: string
}

export const WatchProjectShareSeriesModal: React.FC<WatchProjectShareSeriesModalProps> = ({ slug }) => {
  const [isOpen, setIsOpen] = useState(false)
  const { t } = useTranslate('watch')

  const { seasons } = useShareBrandContent(slug)

  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash

      if (hash === '#share-series-modal') {
        setIsOpen(true)
      }
      window.location.hash = ''
    }

    window.addEventListener('hashchange', handleHashChange)

    handleHashChange()

    return () => {
      window.removeEventListener('hashchange', handleHashChange)
    }
  }, [])

  return (
    <AngelDialog
      className="bottom-0 !items-end !justify-end overflow-y-hidden"
      bodyClassName="w-full !max-w-full h-[80vh] overflow-y-hidden !top-[unset] !bottom-0 !my-0 !mb-0 !rounded-b-none bg-core-gray-950 flex flex-col"
      title={t('shareFullAccess', 'Share Full Access')}
      open={isOpen}
      onClose={() => setIsOpen(false)}
    >
      <CaptionMD color="core-gray-400" className="mb-6 max-w-[800px] shrink-0 grow-0 text-balance">
        <Translate t={t} i18nKey="asAMemberYouCanShare">
          As a Guild member you can share free access to our movies, series and specials. Share as many as you&apos;d
          like and help spread shows that amplify light! Which episode would you like to share?
        </Translate>
      </CaptionMD>

      <div className="grow-1 overflow-y-auto scrollbar-thin scrollbar-track-core-gray-950 scrollbar-thumb-core-gray-700">
        {seasons.map((season, i) => {
          const { seasonNumber } = season
          return (
            <div key={`share-season-${i}`}>
              <div className="mb-6 w-full bg-core-gray-900 px-6 py-3">
                <TitleSM className="uppercase">
                  <Translate t={t} i18nKey="seasonNumber" values={{ seasonNumber }}>
                    Season {{ seasonNumber }}
                  </Translate>
                </TitleSM>
              </div>
              <LargeLandscapeGrid
                showTitleDescription
                imageWidth={256}
                imageHeight={144}
                className="items-center justify-center justify-items-center"
                id={`share-season-${i}`}
                links={season.episodes?.map((episode) => {
                  const episodeName = t('episodeNumber', `Episode {{episodeNumber}}`, {
                    episodeNumber: episode.episodeNumber,
                  })
                  return {
                    linkUrl: `${paths.watch.index}/${slug}/episode/${episode.id}?share=true`,
                    imageUrl: episode.posterLandscapeCloudinaryPath,
                    projectType: 'series',
                    linkType: 'episode',
                    name: episodeName,
                    label: episode.name as string,
                  }
                })}
              />
            </div>
          )
        })}
      </div>
    </AngelDialog>
  )
}

const GET_BRAND_SHARE_DATA = gql`
  ${CONTENT_AVAIL_FRAG}
  query getBrandShareData($slug: String!) {
    project(slug: $slug) {
      id
      title {
        __typename
        ... on ContentSeries {
          id
          seasons(first: 10) {
            edges {
              node {
                id
                seasonNumber
                name
                episodes(first: 50) {
                  edges {
                    node {
                      __typename
                      id
                      name
                      subtitle
                      episodeNumber
                      isGuildShareAvailable
                      posterLandscapeCloudinaryPath
                      ...ContentAvailability
                    }
                  }
                }
              }
            }
          }
        }
        ... on ContentMovie {
          id
        }
        ... on ContentDisplayable {
          image(aspect: "16:9", category: ANGEL_KEY_ART_2) {
            cloudinaryPath
          }
        }
        ...ContentAvailability
      }
    }
  }
`

export const useShareBrandContent = (slug: string) => {
  const { data } = useQuery<GetBrandShareDataQuery, GetBrandShareDataQueryVariables>(GET_BRAND_SHARE_DATA, {
    variables: { slug },
  })

  const formattedSeasons = useMemo(() => {
    if (data?.project?.title?.__typename !== 'ContentSeries') return []

    const seasons = data.project.title.seasons?.edges ?? []
    const filteredSeasons = seasons
      .filter(
        (season): season is NonNullable<typeof season> & { node: NonNullable<NonNullable<typeof season>['node']> } =>
          !!season?.node?.episodes?.edges?.some(
            (episode) => episode?.node?.watchableAvailabilityStatus === 'WATCHABLE',
          ),
      )
      .map((season) => ({
        ...season.node,
        episodes:
          season.node.episodes?.edges
            ?.map((edge) => edge?.node)
            .filter(
              (episode): episode is NonNullable<typeof episode> =>
                !!episode &&
                /**
                 * We have some fake episodes that have an episode number of 0 or less so they appear first in the list
                 * e.g. homestead movie recap
                 */
                !!episode.isGuildShareAvailable &&
                !!episode.episodeNumber &&
                episode.episodeNumber > 0 &&
                episode.watchableAvailabilityStatus === 'WATCHABLE',
            ) ?? [],
      }))

    return filteredSeasons
  }, [data?.project?.title])

  const bannerImage = useMemo(() => {
    if (data?.project?.title?.__typename === 'ContentMovie' || data?.project?.title?.__typename === 'ContentSeries')
      return data.project.title.image?.cloudinaryPath ?? ''

    return ''
  }, [data?.project?.title])

  const hasShareableContent = useMemo(() => {
    return formattedSeasons.length > 0
  }, [formattedSeasons])

  return {
    bannerImage,
    seasons: formattedSeasons,
    hasShareableContent,
  }
}
